/**
 * Markers — creates accessible MapLibre markers with HIPAA-safe popups.
 * All outbound links include rel="noopener noreferrer".
 * Markers are keyboard-focusable with proper ARIA labels.
 */

import { DIRECTIONS } from './config.js';

// =====================================================
// Hours Parsing & Status (unchanged from Leaflet version)
// =====================================================

function getOpenStatus(loc) {
  const hoursStr = loc.hours || loc._raw?.hours;
  if (hoursStr) return getStatusFromHoursString(hoursStr);

  const openStr = loc.open_time || loc._raw?.open_time;
  const closeStr = loc.close_time || loc._raw?.close_time;
  if (!openStr || !closeStr) return null;

  const openMinutes = parseTimeToMinutes(openStr);
  const closeMinutes = parseTimeToMinutes(closeStr);
  if (openMinutes === null || closeMinutes === null) return null;

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  if (openMinutes <= closeMinutes) {
    return (nowMinutes >= openMinutes && nowMinutes < closeMinutes) ? 'open' : 'closed';
  }
  return (nowMinutes >= openMinutes || nowMinutes < closeMinutes) ? 'open' : 'closed';
}

function getStatusFromHoursString(hoursStr) {
  const normalized = hoursStr.trim().toLowerCase();
  if (normalized === '24/7' || normalized === 'open 24/7' || normalized === '24 hours' || normalized === 'open 24 hours') {
    return 'open';
  }

  const DAY_MAP = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const now = new Date();
  const todayIdx = now.getDay();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const segments = hoursStr.split(';').map(s => s.trim()).filter(Boolean);

  for (const segment of segments) {
    const colonIdx = segment.indexOf(':');
    if (colonIdx === -1) continue;

    const daysPart = segment.substring(0, colonIdx).trim().toLowerCase();
    const timesPart = segment.substring(colonIdx + 1).trim();

    if (!dayMatchesToday(daysPart, todayIdx, DAY_MAP)) continue;

    if (timesPart.toLowerCase() === 'closed') return 'closed';
    if (timesPart.toLowerCase() === '24 hours') return 'open';

    const timeParts = timesPart.split(/\s*[-–—]\s*/);
    if (timeParts.length !== 2) continue;

    const openMinutes = parseTimeToMinutes(timeParts[0]);
    const closeMinutes = parseTimeToMinutes(timeParts[1]);
    if (openMinutes === null || closeMinutes === null) continue;

    if (openMinutes <= closeMinutes) {
      return (nowMinutes >= openMinutes && nowMinutes < closeMinutes) ? 'open' : 'closed';
    }
    return (nowMinutes >= openMinutes || nowMinutes < closeMinutes) ? 'open' : 'closed';
  }

  return null;
}

function dayMatchesToday(daysPart, todayIdx, DAY_MAP) {
  const rangeMatch = daysPart.match(/^(\w{3})\s*[-–—]\s*(\w{3})$/);
  if (rangeMatch) {
    const startIdx = DAY_MAP[rangeMatch[1]];
    const endIdx = DAY_MAP[rangeMatch[2]];
    if (startIdx === undefined || endIdx === undefined) return false;
    if (startIdx <= endIdx) return todayIdx >= startIdx && todayIdx <= endIdx;
    return todayIdx >= startIdx || todayIdx <= endIdx;
  }
  const days = daysPart.split(/\s*,\s*/);
  return days.some(d => DAY_MAP[d.trim()] === todayIdx);
}

function parseTimeToMinutes(str) {
  if (!str) return null;
  const s = str.trim().toLowerCase();

  const match12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = parseInt(match12[2] || '0', 10);
    const period = match12[3];
    if (period === 'am' && hours === 12) hours = 0;
    if (period === 'pm' && hours !== 12) hours += 12;
    return hours * 60 + minutes;
  }

  const match24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    return parseInt(match24[1], 10) * 60 + parseInt(match24[2], 10);
  }

  return null;
}

function getAcceptingStatus(loc) {
  const val = (loc.accepting_new_patients || loc._raw?.accepting_new_patients || '').trim().toLowerCase();
  if (!val) return null;

  if (val === 'yes') return { text: 'Accepting New Patients', className: 'pm-accepting-yes', icon: '✓' };
  if (val === 'no') return { text: 'Not Accepting New Patients', className: 'pm-accepting-no', icon: '✕' };
  if (val === 'call') return { text: 'Call for Availability', className: 'pm-accepting-call', icon: '✆' };
  return null;
}


// =====================================================
// Popup HTML Builder (unchanged)
// =====================================================

function buildPopupHTML(loc, config, compact) {
  const status = getOpenStatus(loc);
  const accepting = getAcceptingStatus(loc);
  const directionsUrl = buildDirectionsUrl(loc, config.directions);

  let html = `<div class="pm-popup ${compact ? 'pm-popup-compact' : ''}">`;

  if (compact && loc.photo) {
    html += '<div class="pm-popup-row">';
    html += `<div class="pm-popup-thumb">
      <img src="${escapeHTML(loc.photo)}" alt="${escapeHTML(loc.name)}" loading="lazy">
    </div>`;
    html += '<div class="pm-popup-content">';
  } else if (loc.photo) {
    html += `<div class="pm-popup-image">
      <img src="${escapeHTML(loc.photo)}" alt="${escapeHTML(loc.name)}" loading="lazy">
    </div>`;
  }

  html += '<div class="pm-popup-header">';
  html += `<h4>${escapeHTML(loc.name)}</h4>`;
  if (status) {
    const cls = status === 'open' ? 'pm-status-open' : 'pm-status-closed';
    html += `<span class="pm-status-badge ${cls}">${status === 'open' ? 'Open Now' : 'Closed'}</span>`;
  }
  html += '</div>';

  if (accepting) {
    html += `<div class="pm-accepting ${accepting.className}">
      <span class="pm-accepting-icon" aria-hidden="true">${accepting.icon}</span>
      ${escapeHTML(accepting.text)}
    </div>`;
  }

  html += '<div class="pm-popup-body">';
  config.popupFields.forEach(field => {
    const value = loc[field] || loc._raw?.[field];
    if (!value) return;

    if (field === 'phone') {
      const cleanPhone = value.replace(/[^\d+]/g, '');
      html += `<p class="pm-popup-field pm-popup-phone">
        <span class="pm-sr-only">Phone: </span>
        <a href="tel:${cleanPhone}" aria-label="Call ${escapeHTML(loc.name)} at ${escapeHTML(value)}">${escapeHTML(value)}</a>
      </p>`;
    } else if (field === 'fax') {
      html += `<p class="pm-popup-field"><span class="pm-sr-only">Fax: </span>${escapeHTML(value)}</p>`;
    } else if (field === 'address') {
      html += `<p class="pm-popup-field pm-popup-address"><span class="pm-sr-only">Address: </span>${escapeHTML(value)}</p>`;
    } else if (field === 'hours') {
      html += `<p class="pm-popup-field pm-popup-hours"><span class="pm-sr-only">Hours: </span>${escapeHTML(value)}</p>`;
    } else if (field === 'url' || field === 'website') {
      html += `<p class="pm-popup-field"><a href="${escapeHTML(value)}" target="_blank" rel="noopener noreferrer" aria-label="Visit ${escapeHTML(loc.name)} website">Website</a></p>`;
    } else if (field === 'services') {
      html += `<p class="pm-popup-field pm-popup-services"><span class="pm-sr-only">Services: </span>${escapeHTML(value)}</p>`;
    } else {
      html += `<p class="pm-popup-field">${escapeHTML(value)}</p>`;
    }
  });

  if ((loc.open_time || loc._raw?.open_time) && !config.popupFields.includes('hours')) {
    const openStr = loc.open_time || loc._raw?.open_time;
    const closeStr = loc.close_time || loc._raw?.close_time;
    if (openStr && closeStr) {
      html += `<p class="pm-popup-field"><span class="pm-sr-only">Hours: </span>${escapeHTML(openStr)} – ${escapeHTML(closeStr)}</p>`;
    }
  }
  html += '</div>';

  html += `<a class="pm-popup-directions-btn"
    href="${directionsUrl}"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Get directions to ${escapeHTML(loc.name)}">
    <span class="pm-directions-icon">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="3 11 22 2 13 21 11 13 3 11"/>
      </svg>
    </span>
    Get Directions
  </a>`;

  if (compact && loc.photo) {
    html += '</div></div>';
  }

  html += '</div>';
  return html;
}

function buildDirectionsUrl(loc, provider) {
  if (provider === 'apple') return DIRECTIONS.apple(loc.address || `${loc.lat},${loc.lng}`);
  if (provider === 'geo') return DIRECTIONS.geo(loc.lat, loc.lng);
  return DIRECTIONS.google(loc.address || `${loc.lat},${loc.lng}`);
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}


// =====================================================
// MapLibre Marker & Popup Creation
// =====================================================

/**
 * Create a custom marker DOM element (SVG pin in brand color).
 */
function createMarkerElement(brandColor) {
  const el = document.createElement('div');
  el.className = 'pm-marker-icon';
  el.innerHTML = `<svg width="28" height="38" viewBox="0 0 28 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 24 14 24s14-13.5 14-24C28 6.268 21.732 0 14 0z" fill="${brandColor}"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
  </svg>`;
  el.style.cursor = 'pointer';
  el.style.width = '28px';
  el.style.height = '38px';
  return el;
}

/**
 * Add all markers to the map with accessibility attributes.
 *
 * @param {maplibregl.Map} map
 * @param {Array} locations
 * @param {Object} config
 * @returns {{ markers: Array<{marker, popup, element, location, index}> }}
 */
export function addMarkers(map, locations, config) {
  const maplibregl = window.maplibregl;
  const markers = [];

  // Detect if map container is short — use compact popup layout
  const mapContainer = map.getContainer();
  const compact = mapContainer.offsetHeight < 500;

  // Track which popup is currently open for zoom re-centering
  let activePopupLngLat = null;
  let activePopup = null;

  locations.forEach((loc, index) => {
    const el = createMarkerElement(config.brandColor);

    // A11y: make marker focusable
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');

    const labelParts = [loc.name];
    const status = getOpenStatus(loc);
    if (status) labelParts.push(status === 'open' ? 'Open now' : 'Closed');
    const accepting = getAcceptingStatus(loc);
    if (accepting) labelParts.push(accepting.text);
    if (loc.specialty) labelParts.push(loc.specialty);
    labelParts.push('Press Enter for details.');
    el.setAttribute('aria-label', labelParts.join('. '));

    // Create marker
    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([loc.lng, loc.lat])
      .addTo(map);

    // Create popup
    const popupHtml = buildPopupHTML(loc, config, compact);
    const popup = new maplibregl.Popup({
      maxWidth: window.innerWidth <= 640 ? '220px' : (compact ? '400px' : '300px'),
      closeButton: true,
      closeOnClick: false,
      anchor: 'bottom', offset: [0, -38],
      className: 'pm-maplibre-popup',
    }).setHTML(popupHtml);

    // Link popup to marker
    marker.setPopup(popup);

    // Popup open handler — a11y enhancements
    popup.on('open', () => {
      // Close any other open popup
      if (activePopup && activePopup !== popup && activePopup.isOpen()) {
        activePopup.remove();
      }
      activePopupLngLat = [loc.lng, loc.lat];
      activePopup = popup;

      // Auto-pan to ensure popup is fully visible
      setTimeout(() => {
        const popupEl = popup.getElement();
        if (!popupEl) return;
        const container = map.getContainer();
        const mapRect = container.getBoundingClientRect();
        const popupRect = popupEl.getBoundingClientRect();
        const popupHeight = popupRect.height;
        const mapHeight = container.offsetHeight;
        
        // On small maps / mobile, position marker so popup fits above
        if (popupHeight > mapHeight * 0.5) {
          // Popup is tall relative to map — put marker near bottom
          const markerPoint = map.project([loc.lng, loc.lat]);
          const targetY = mapHeight - 50;
          const dy = markerPoint.y - targetY;
          if (Math.abs(dy) > 5) {
            map.panBy([0, dy], { animate: true, duration: 300 });
          }
        } else {
          // Normal auto-pan
          let needsPan = false;
          let dx = 0, dy = 0;
          const pad = 40;
          if (popupRect.top < mapRect.top + pad) {
            dy = popupRect.top - mapRect.top - pad;
            needsPan = true;
          }
          if (popupRect.bottom > mapRect.bottom - pad) {
            dy = popupRect.bottom - mapRect.bottom + pad;
            needsPan = true;
          }
          if (popupRect.left < mapRect.left + pad) {
            dx = popupRect.left - mapRect.left - pad;
            needsPan = true;
          }
          if (popupRect.right > mapRect.right - pad) {
            dx = popupRect.right - mapRect.right + pad;
            needsPan = true;
          }
          if (needsPan) {
            map.panBy([dx, dy], { animate: true, duration: 300 });
          }
        }
      }, 50);

      const popupEl = popup.getElement();
      if (popupEl) {
        const container = popupEl.querySelector('.pm-popup');
        if (container) {
          container.setAttribute('role', 'dialog');
          container.setAttribute('aria-label', `Details for ${loc.name}`);

          const firstFocusable = container.querySelector('a, button, [tabindex]');
          if (firstFocusable) {
            setTimeout(() => firstFocusable.focus(), 50);
          }
        }

        // Label close button
        const closeBtn = popupEl.querySelector('.maplibregl-popup-close-button');
        if (closeBtn) {
          closeBtn.setAttribute('aria-label', `Close details for ${loc.name}`);
          closeBtn.setAttribute('role', 'button');
        }
      }
    });

    popup.on('close', () => {
      if (activePopup === popup) {
        activePopupLngLat = null;
        activePopup = null;
      }
    });

    // Keyboard: Enter/Space opens popup
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        marker.togglePopup();
      }
    });

    markers.push({ marker, popup, element: el, location: loc, index });
  });

  // When user zooms while a popup is open, re-center on that marker
  map.on('zoomend', () => {
    if (activePopupLngLat && activePopup && activePopup.isOpen()) {
      setTimeout(() => {
        const markerPoint = map.project(activePopupLngLat);
        const container = map.getContainer();
        const w = container.offsetWidth;
        const h = container.offsetHeight;
        // Place marker at 70% down the viewport so popup has room above
        const targetX = w / 2;
        const targetY = h * 0.7;
        const dx = markerPoint.x - targetX;
        const dy = markerPoint.y - targetY;
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
          map.panBy([dx, dy], { animate: true, duration: 300 });
        }
      }, 100);
    }
  });

  return { markers };
}

export { getOpenStatus, getAcceptingStatus, buildPopupHTML };
